import { useCallback, useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useAuth } from '../auth/AuthContext'
import { useI18n } from '../i18n/I18nProvider'
import type { MessageKey } from '../i18n/messages'
import { DateLisible, Nombre } from '../components/Nombre'
import {
  Alert,
  Badge,
  Button,
  Card,
  CheckboxField,
  DataTable,
  DescriptionItem,
  DescriptionList,
  FormActions,
  Grid,
  PageHeader,
  SelectField,
  Stack,
  TextField,
  useToast,
} from '../components/ui'
import type { Column } from '../components/ui'
import { PAYMENT_METHODS, billingApi, enregistrer, toneOf } from './billingApi'
import type { InvoiceDocument, PaymentMethod } from './billingApi'

type Ligne = InvoiceDocument['lines'][number]
type Paiement = InvoiceDocument['payments'][number]

/** « 1 234,5 » ou « 1234.5 » → 1234.5 ; ce qui n'est pas un nombre donne NaN, la base juge le reste. */
function lireMontant(saisie: string): number {
  return Number(saisie.replace(/\s/g, '').replace(',', '.'))
}

/**
 * Une facture ou un avoir : le document figé, ses paiements, ses avoirs, son PDF.
 *
 * RIEN NE SE MODIFIE ICI, et c'est la règle de la base : une facture émise se corrige par un
 * avoir, un paiement mal saisi s'annule avec un motif et reste visible, barré.
 *
 * LE PAIEMENT QUI SOLDE REND LA MAIN AU CLIENT : la base réactive l'abonnement en attente de
 * paiement ou suspendu, et la réponse le dit — l'écran le répète au super-administrateur, qui
 * n'a plus à aller cliquer « Réactiver ».
 */
export function InvoiceDetailPage() {
  const { t, describeError } = useI18n()
  const { session } = useAuth()
  const { notify } = useToast()
  const { id } = useParams()

  const token = session?.token ?? ''
  const factureId = Number(id)

  const [doc, setDoc] = useState<InvoiceDocument | null>(null)
  const [envoi, setEnvoi] = useState(false)

  // Paiement
  const [montant, setMontant] = useState('')
  const [datePaiement, setDatePaiement] = useState('')
  const [moyen, setMoyen] = useState<PaymentMethod>('transfer')
  const [reference, setReference] = useState('')

  // Annulation d'un paiement
  const [annulation, setAnnulation] = useState<Paiement | null>(null)
  const [motifAnnulation, setMotifAnnulation] = useState('')

  // Avoir
  const [avoirTotal, setAvoirTotal] = useState(true)
  const [montantAvoir, setMontantAvoir] = useState('')
  const [motifAvoir, setMotifAvoir] = useState('')

  const charger = useCallback(() => {
    if (token === '' || !Number.isFinite(factureId)) return
    billingApi
      .getInvoice(token, factureId)
      .then((d) => {
        setDoc(d)
        setMontant(d.balance > 0 ? d.balance.toFixed(3) : '')
      })
      .catch((cause: unknown) => notify(describeError(cause), 'erreur'))
  }, [token, factureId, notify, describeError])

  useEffect(charger, [charger])

  const agir = async (action: () => Promise<string>) => {
    setEnvoi(true)
    try {
      notify(await action(), 'succes')
      charger()
    } catch (cause: unknown) {
      notify(describeError(cause), 'erreur')
    } finally {
      setEnvoi(false)
    }
  }

  const telecharger = () =>
    agir(async () => {
      const blob = await billingApi.downloadPdf(token, factureId)
      enregistrer(blob, `${doc?.number ?? 'facture'}.pdf`)
      return t('billing.detail.downloaded')
    })

  const payer = () =>
    agir(async () => {
      const r = await billingApi.recordPayment(token, factureId, {
        paidOn: datePaiement === '' ? null : datePaiement,
        amount: lireMontant(montant),
        method: moyen,
        reference: reference.trim() === '' ? null : reference.trim(),
        note: null,
      })
      setReference('')
      return t(r.reactivated ? 'billing.payment.reactivated' : 'billing.payment.recorded')
    })

  const annuler = () =>
    agir(async () => {
      if (annulation === null) return ''
      await billingApi.voidPayment(token, annulation.id, motifAnnulation.trim())
      setAnnulation(null)
      setMotifAnnulation('')
      return t('billing.payment.voided')
    })

  const crediter = () =>
    agir(async () => {
      const r = await billingApi.createCreditNote(token, factureId, {
        amountHt: avoirTotal ? null : lireMontant(montantAvoir),
        reason: motifAvoir.trim(),
      })
      setMotifAvoir('')
      setMontantAvoir('')
      return `${t('billing.credit.created')} #${r.id}`
    })

  if (doc === null) {
    return <p className="text-muted">{t('common.loading')}</p>
  }

  const avoir = doc.kind === 'credit_note'
  const ouverte = !avoir && doc.status !== 'cancelled'

  const colonnesLignes: Column<Ligne>[] = [
    { key: 'description', header: t('billing.detail.description'), render: (l) => l.description },
    { key: 'quantity', header: t('billing.detail.quantity'), numeric: true, render: (l) => <Nombre valeur={l.quantity} decimales={Number.isInteger(l.quantity) ? 0 : 3} /> },
    { key: 'unitPrice', header: t('billing.detail.unitPrice'), numeric: true, render: (l) => <Nombre valeur={l.unitPrice} decimales={3} /> },
    { key: 'amountHt', header: t('billing.detail.amountHt'), numeric: true, render: (l) => <Nombre valeur={l.amountHt} decimales={3} /> },
  ]

  const colonnesPaiements: Column<Paiement>[] = [
    { key: 'paidOn', header: t('billing.payment.date'), render: (p) => <DateLisible valeur={p.paidOn} /> },
    {
      key: 'amount',
      header: t('billing.payment.amount'),
      numeric: true,
      render: (p) => (
        <span style={p.voidedAt !== null ? { textDecoration: 'line-through', opacity: 0.6 } : undefined}>
          <Nombre valeur={p.amount} decimales={3} />
        </span>
      ),
    },
    { key: 'method', header: t('billing.payment.method'), render: (p) => t(`billing.method.${p.method}` as MessageKey) },
    { key: 'reference', header: t('billing.payment.reference'), render: (p) => p.reference ?? '—', secondary: true },
    {
      key: 'state',
      header: t('billing.col.status'),
      render: (p) =>
        p.voidedAt !== null ? (
          <span title={p.voidReason ?? ''}>
            <Badge tone="erreur">{t('billing.payment.voidedBadge')}</Badge>
          </span>
        ) : (
          <Button variant="discret" onClick={() => setAnnulation(p)}>
            {t('billing.payment.void')}
          </Button>
        ),
    },
  ]

  return (
    <Stack>
      <PageHeader
        title={`${t(avoir ? 'billing.kind.credit_note' : 'billing.kind.invoice')} ${doc.number}`}
        description={doc.buyer.legalName}
        actions={
          <Stack gap={2}>
            <Button onClick={telecharger} pending={envoi}>
              {t('billing.detail.pdf')}
            </Button>
          </Stack>
        }
      />

      <Link to="/platform/invoices">← {t('billing.detail.back')}</Link>

      <Card title={t('billing.detail.summary')}>
        <DescriptionList>
          <DescriptionItem label={t('billing.col.status')}>
            <Badge tone={toneOf(doc.status)}>{t(`billing.status.${doc.status}` as MessageKey)}</Badge>
          </DescriptionItem>
          <DescriptionItem label={t('billing.col.client')}>
            <Link to={`/platform/clients/${doc.organizationId}`}>{doc.buyer.legalName}</Link>
            {doc.buyer.taxId !== null && <span className="text-muted"> · {doc.buyer.taxId}</span>}
          </DescriptionItem>
          <DescriptionItem label={t('billing.col.issuedOn')}>
            <DateLisible valeur={doc.issuedOn} />
          </DescriptionItem>
          {!avoir && (
            <DescriptionItem label={t('billing.col.dueOn')}>
              <DateLisible valeur={doc.dueOn} />
            </DescriptionItem>
          )}
          {doc.periodStart !== null && doc.periodEnd !== null && (
            <DescriptionItem label={t('billing.detail.period')}>
              <DateLisible valeur={doc.periodStart} /> → <DateLisible valeur={doc.periodEnd} />
            </DescriptionItem>
          )}
          {doc.creditedInvoice !== null && (
            <DescriptionItem label={t('billing.detail.credited')}>
              <Link to={`/platform/invoices/${doc.creditedInvoice.id}`}>{doc.creditedInvoice.number}</Link>
            </DescriptionItem>
          )}
          {doc.reason !== null && <DescriptionItem label={t('billing.credit.reason')}>{doc.reason}</DescriptionItem>}
          {doc.note !== null && <DescriptionItem label={t('billing.new.note')}>{doc.note}</DescriptionItem>}
        </DescriptionList>
      </Card>

      <Card title={t('billing.detail.lines')}>
        <Stack gap={3}>
          <DataTable columns={colonnesLignes} rows={doc.lines} rowKey={(l) => l.position} emptyLabel="—" />
          <DescriptionList>
            <DescriptionItem label={t('billing.detail.totalHt')}>
              <Nombre valeur={doc.totalHt} decimales={3} />
            </DescriptionItem>
            <DescriptionItem label={t('billing.detail.vat')}>
              <Nombre valeur={doc.vatRate} decimales={0} unite="%" /> — <Nombre valeur={doc.totalVat} decimales={3} />
            </DescriptionItem>
            <DescriptionItem label={t('billing.detail.stamp')}>
              <Nombre valeur={doc.stampDuty} decimales={3} />
            </DescriptionItem>
            <DescriptionItem label={t('billing.detail.totalTtc')}>
              <strong>
                <Nombre valeur={doc.totalTtc} decimales={3} unite={doc.currencyCode} />
              </strong>
            </DescriptionItem>
            {!avoir && (
              <DescriptionItem label={t('billing.col.balance')}>
                <strong>
                  <Nombre valeur={doc.balance} decimales={3} unite={doc.currencyCode} />
                </strong>
              </DescriptionItem>
            )}
          </DescriptionList>
        </Stack>
      </Card>

      {!avoir && (
        <>
          <Card title={t('billing.detail.payments')}>
            <Stack gap={3}>
              <DataTable columns={colonnesPaiements} rows={doc.payments} rowKey={(p) => p.id} emptyLabel={t('billing.detail.noPayments')} />

              {annulation !== null && (
                <Alert tone="avertissement" title={t('billing.payment.voidTitle')}>
                  <Stack gap={2}>
                    <TextField label={t('billing.payment.voidReason')} value={motifAnnulation} onChange={setMotifAnnulation} required />
                    <FormActions>
                      <Button variant="secondaire" onClick={() => setAnnulation(null)}>
                        {t('common.cancel')}
                      </Button>
                      <Button variant="danger" onClick={annuler} disabled={motifAnnulation.trim() === ''} pending={envoi}>
                        {t('billing.payment.void')}
                      </Button>
                    </FormActions>
                  </Stack>
                </Alert>
              )}

              {ouverte && doc.balance > 0 && (
                <Stack gap={2}>
                  <strong>{t('billing.payment.title')}</strong>
                  <Grid min="10rem">
                    <TextField label={t('billing.payment.amount')} value={montant} onChange={setMontant} ltr required />
                    <TextField label={t('billing.payment.date')} type="date" value={datePaiement} onChange={setDatePaiement} hint={t('billing.payment.dateHint')} />
                    <SelectField
                      label={t('billing.payment.method')}
                      value={moyen}
                      onChange={(v) => setMoyen(v as PaymentMethod)}
                      options={PAYMENT_METHODS.map((m) => ({ value: m, label: t(`billing.method.${m}` as MessageKey) }))}
                    />
                    <TextField label={t('billing.payment.reference')} value={reference} onChange={setReference} ltr />
                  </Grid>
                  <FormActions>
                    <Button onClick={payer} disabled={montant.trim() === ''} pending={envoi}>
                      {t('billing.payment.submit')}
                    </Button>
                  </FormActions>
                </Stack>
              )}
            </Stack>
          </Card>

          <Card title={t('billing.detail.credits')}>
            <Stack gap={3}>
              {doc.creditNotes.length === 0 ? (
                <p className="text-muted">{t('billing.detail.noCredits')}</p>
              ) : (
                <ul>
                  {doc.creditNotes.map((c) => (
                    <li key={c.id}>
                      <Link to={`/platform/invoices/${c.id}`}>{c.number}</Link> — <Nombre valeur={c.totalTtc} decimales={3} /> — {c.reason}
                    </li>
                  ))}
                </ul>
              )}

              {ouverte && (
                <Stack gap={2}>
                  <strong>{t('billing.credit.title')}</strong>
                  <CheckboxField label={t('billing.credit.full')} checked={avoirTotal} onChange={setAvoirTotal} />
                  {!avoirTotal && (
                    <TextField label={t('billing.credit.amountHt')} value={montantAvoir} onChange={setMontantAvoir} ltr required />
                  )}
                  <TextField label={t('billing.credit.reason')} value={motifAvoir} onChange={setMotifAvoir} required />
                  <FormActions>
                    <Button
                      variant="danger"
                      onClick={crediter}
                      disabled={motifAvoir.trim() === '' || (!avoirTotal && montantAvoir.trim() === '')}
                      pending={envoi}
                    >
                      {t('billing.credit.submit')}
                    </Button>
                  </FormActions>
                </Stack>
              )}
            </Stack>
          </Card>
        </>
      )}
    </Stack>
  )
}
